import { ReactNode } from 'react';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  width?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  emptyMessage?: string;
  loading?: boolean;
}

/**
 * Table component — responsive, accessible with proper ARIA roles.
 */
export function Table<T extends Record<string, any>>({
  columns,
  data,
  keyField,
  emptyMessage = 'Nessun dato disponibile',
  loading,
}: TableProps<T>) {
  if (loading) {
    return (
      <div className="table-loading" role="status" aria-label="Caricamento tabella">
        <div className="spinner spinner-sm" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="table-empty" role="status">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="table-wrapper" role="region" aria-label="Tabella dati" tabIndex={0}>
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={String(item[keyField])}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render ? col.render(item) : String(item[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
